import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Copy, Trash2, ExternalLink, ShieldCheck, Mail, Send, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PersonAvatar from "@/components/PersonAvatar";

const SIG_STORAGE_KEY = "ayobell.digest.signature";

export default function FamilyPortal() {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", family_name: "", relationship: "", family_email: "" });
  const [saving, setSaving] = useState(false);
  const [sendingDigestId, setSendingDigestId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  // Signature state — persisted so managers don't retype every send
  const [sig, setSig] = useState(() => {
    try {
      const raw = localStorage.getItem(SIG_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { name: "", role: "" };
  });
  const [sigOpen, setSigOpen] = useState(false);

  // Default the signature to the logged-in user's name & role on first use
  useEffect(() => {
    if (!sig.name && user) {
      setSig({ name: user.name || user.email || "", role: (user.role || "").replace(/^./, (c) => c.toUpperCase()) });
    }
  }, [user, sig.name]);

  const saveSig = (next) => {
    setSig(next);
    try { localStorage.setItem(SIG_STORAGE_KEY, JSON.stringify(next)); } catch { /* noop */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([api.get("/portal/links"), api.get("/clients")]);
      setLinks(l.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.client_id) { toast.error("Pick a service user"); return; }
    setSaving(true);
    try {
      await api.post("/portal/links", form);
      toast.success("Family link created");
      setOpen(false);
      setForm({ client_id: "", family_name: "", relationship: "", family_email: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this family link? The recipient will lose access.")) return;
    try {
      await api.delete(`/portal/links/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const publicUrl = (token) => `${window.location.origin}/family/${token}`;
  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(publicUrl(token));
      toast.success("Link copied");
    } catch { toast.error("Could not copy"); }
  };

  const active = links.filter((l) => !l.revoked);
  const revoked = links.filter((l) => l.revoked);

  const withSigQuery = () => {
    const params = new URLSearchParams();
    if (sig.name) params.set("signed_by_name", sig.name);
    if (sig.role) params.set("signed_by_role", sig.role);
    const q = params.toString();
    return q ? `?${q}` : "";
  };

  const ensureSig = () => {
    if (!sig.name.trim()) {
      setSigOpen(true);
      toast.error("Please add your signature first — families need to know who prepared the digest");
      return false;
    }
    return true;
  };

  const sendDigest = async (linkId) => {
    if (!ensureSig()) return;
    setSendingDigestId(linkId);
    const id = toast.loading("Queuing digest…");
    try {
      const r = await api.post("/portal/digest/send", {
        link_id: linkId,
        signed_by_name: sig.name,
        signed_by_role: sig.role,
      });
      toast.success(`Digest queued · signed by ${sig.name} · ${r.data.channels.join(" + ")}`, { id });
    } catch (e) { toast.error(formatApiError(e), { id }); }
    finally { setSendingDigestId(null); }
  };

  const sendDigestAll = async () => {
    if (!ensureSig()) return;
    if (!window.confirm(`Send this week's care digest to all ${active.length} active family link(s), signed as ${sig.name}?`)) return;
    setSendingAll(true);
    const id = toast.loading("Queuing digests for all families…");
    try {
      const r = await api.post("/portal/digest/send", {
        signed_by_name: sig.name,
        signed_by_role: sig.role,
      });
      toast.success(`Queued ${r.data.queued} digest(s) via ${r.data.channels.join(" + ")}`, { id });
    } catch (e) { toast.error(formatApiError(e), { id }); }
    finally { setSendingAll(false); }
  };

  const previewDigest = async (linkId, clientName) => {
    if (!ensureSig()) return;
    const id = toast.loading("Preparing signed digest PDF…");
    try {
      await downloadBlob(`/portal/links/${linkId}/digest.pdf${withSigQuery()}`, `family-digest-${(clientName || "client").replace(/\s+/g, "_")}.pdf`);
      toast.success(`Digest downloaded · signed by ${sig.name}`, { id });
    } catch (e) { toast.error(formatApiError(e), { id }); }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-slate-200 bg-white px-6 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">People</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="portal-title">Family Portal Links</h1>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="btn-add-portal-link" className="h-9 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New family link
        </Button>
      </div>

      <div className="p-6 space-y-6">
        <div className="bg-sky-50 border border-sky-200 p-4 text-sm text-slate-700 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-sky-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            Family portal links give the recipient a <span className="font-semibold">read-only</span> view of recent daily logs and medication administration for one service user. Share only with consented next of kin.
          </div>
          {active.length > 0 && (
            <Button
              size="sm" variant="outline"
              onClick={sendDigestAll}
              disabled={sendingAll}
              data-testid="btn-send-digest-all"
              className="h-8 shrink-0"
            >
              <Send className={`w-3.5 h-3.5 mr-1.5 ${sendingAll ? "animate-pulse" : ""}`} />
              Send weekly digest to all
            </Button>
          )}
        </div>

        {/* Signature strip */}
        <div
          className="bg-white border border-slate-200 p-3 flex items-center gap-3 flex-wrap"
          data-testid="digest-signature-strip"
        >
          <PenLine className="w-4 h-4 text-emerald-700 shrink-0" />
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Digest signed by</div>
            {sig.name ? (
              <div className="text-sm text-slate-900" data-testid="digest-sig-display">
                <span className="font-medium">{sig.name}</span>
                {sig.role && <span className="text-slate-500"> · {sig.role}</span>}
              </div>
            ) : (
              <div className="text-sm text-amber-700" data-testid="digest-sig-missing">Not set — please add your signature before sending digests.</div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => setSigOpen(true)} data-testid="btn-edit-signature" className="h-8">
            <PenLine className="w-3.5 h-3.5 mr-1.5" /> {sig.name ? "Change signature" : "Add signature"}
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500" data-testid="portal-loading">Loading links…</div>
        ) : (
          <>
            <section>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Active</div>
              {active.length === 0 ? (
                <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="portal-empty">No active links yet.</div>
              ) : (
                <ul className="space-y-2" data-testid="portal-list">
                  {active.map((l) => (
                    <li key={l.id} data-testid={`portal-link-${l.id}`} className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-center gap-3">
                      <PersonAvatar person={{ full_name: l.client_name, photo_url: l.client_photo_url, photo_thumb_url: l.client_photo_thumb_url }} name={l.client_name} size="md" testid={`portal-avatar-${l.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{l.client_name}</div>
                        <div className="text-xs text-slate-600">{l.family_name || "Family"}{l.relationship ? ` · ${l.relationship}` : ""}</div>
                        {l.family_email && (
                          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5" data-testid={`portal-email-${l.id}`}>
                            <Mail className="w-3 h-3" /> {l.family_email}
                          </div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-1 truncate">{publicUrl(l.token)}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => previewDigest(l.id, l.client_name)} data-testid={`btn-digest-preview-${l.id}`} className="h-8">
                          <Mail className="w-3.5 h-3.5 mr-1.5" /> Preview digest
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => sendDigest(l.id)}
                          disabled={sendingDigestId === l.id}
                          data-testid={`btn-digest-send-${l.id}`}
                          className="h-8"
                        >
                          <Send className={`w-3.5 h-3.5 mr-1.5 ${sendingDigestId === l.id ? "animate-pulse" : ""}`} /> Send digest
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => copy(l.token)} data-testid={`btn-copy-${l.id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
                        </Button>
                        <a href={publicUrl(l.token)} target="_blank" rel="noreferrer" data-testid={`btn-open-${l.id}`}>
                          <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
                        </a>
                        <Button size="sm" variant="ghost" onClick={() => revoke(l.id)} data-testid={`btn-revoke-${l.id}`} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {revoked.length > 0 && (
              <section>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Revoked</div>
                <ul className="space-y-2 opacity-60">
                  {revoked.map((l) => (
                    <li key={l.id} className="bg-white border border-slate-200 p-3 text-xs text-slate-500">
                      {l.client_name} · revoked {l.revoked_at ? new Date(l.revoked_at).toLocaleDateString() : ""}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="portal-dialog">
          <DialogHeader>
            <DialogTitle>New family portal link</DialogTitle>
            <DialogDescription>Generate a read-only link for a consented family member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Service user</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="portal-client"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Family member name</Label>
              <Input value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} placeholder="e.g. Jane Smith" data-testid="portal-family-name" />
            </div>
            <div>
              <Label>Relationship</Label>
              <Input value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="e.g. Daughter" data-testid="portal-relationship" />
            </div>
            <div>
              <Label>Family email <span className="text-[10px] text-slate-500">(optional — for weekly digest)</span></Label>
              <Input type="email" value={form.family_email} onChange={(e) => setForm({ ...form, family_email: e.target.value })} placeholder="e.g. jane.smith@example.com" data-testid="portal-family-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-save-portal-link" className="bg-slate-900 hover:bg-slate-800">
              {saving ? "Creating…" : "Create link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle>Your digest signature</DialogTitle>
            <DialogDescription>
              Families see this name and role on every digest you prepare. Stored on this device only —
              you can change it at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input
                value={sig.name}
                onChange={(e) => setSig({ ...sig, name: e.target.value })}
                placeholder="e.g. Sarah Okafor"
                data-testid="sig-name"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Input
                value={sig.role}
                onChange={(e) => setSig({ ...sig, role: e.target.value })}
                placeholder="e.g. Registered Manager"
                data-testid="sig-role"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigOpen(false)} data-testid="btn-sig-cancel">Cancel</Button>
            <Button
              onClick={() => { saveSig(sig); setSigOpen(false); toast.success("Signature saved on this device"); }}
              disabled={!sig.name.trim()}
              data-testid="btn-sig-save"
              className="bg-slate-900 hover:bg-slate-800"
            >
              Save signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
